import { FC, memo } from 'react';
import { match } from 'ts-pattern';

import {
  HasDisplayWhen,
  WorkflowCustomStep,
  WorkflowDefaultStepContentBlock,
  WorkflowMirrorStepBlock,
  WorkflowStepFormFieldBlock,
  WorkflowTileBlock,
} from '~/core/shared/types';
import { CustomStepRenderer } from '../../CustomStepRenderer.component';
import { MirrorBlockRenderer } from '../../MirrorBlockRerender.component';
import { OptionallyDisplayWhen } from '../../OptionallyDisplayWhen';
import { TileBlockRenderer } from '../../TileBlockRenderer.component';
import { useBlockRenderer } from './hooks/useBlockRenderer';
import { BlockRendererProps } from './types/BlockRendererProps';
import { ContentBlockRenderer } from './ContentBlockRenderer';
import { FormFieldBlockRenderer } from './FormFieldBlockRenderer';

const BlockRendererComponent: FC<BlockRendererProps> = ({
  block,
  namePrefixes,
  workflowDataResolverDependencies: propsWorkflowDataResolverDependencies,
}) => {
  const { workflowDataResolverDependencies } = useBlockRenderer(
    propsWorkflowDataResolverDependencies,
    namePrefixes,
  );

  return (
    <OptionallyDisplayWhen
      evaluatable={block.displayWhen}
      workflowDataResolverDependencies={workflowDataResolverDependencies}
    >
      {match(block.type)
        .with('content', () => (
          <ContentBlockRenderer
            {...(block as WorkflowDefaultStepContentBlock)}
            workflowDataResolverDependencies={workflowDataResolverDependencies}
          />
        ))
        .with('custom', () => (
          <CustomStepRenderer
            step={block as WorkflowCustomStep & HasDisplayWhen}
          />
        ))
        .with('tile', () => (
          <TileBlockRenderer {...(block as WorkflowTileBlock)} />
        ))
        .with('mirror-event', () => (
          <MirrorBlockRenderer {...(block as WorkflowMirrorStepBlock)} />
        ))
        .with('form-field', () => (
          <FormFieldBlockRenderer
            block={block as WorkflowStepFormFieldBlock}
            namePrefixes={namePrefixes}
          />
        ))
        .otherwise(() => (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <></>
        ))}
    </OptionallyDisplayWhen>
  );
};

export const BlockRenderer = memo(BlockRendererComponent);
